import { setPipelineFilters } from 'actions'
import { isServicingLoan } from 'components/GlobalLoanSearch/utils'
import { AccountType, PipeLineType, PipeLineTypeLabels, userHasAccountType } from 'config'
import { buildPeerPipelineType } from 'config/nexysShareFeatures'
import { usePermissions } from 'hooks/usePermissions'
import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { filterObject, loanNumberForPath } from 'utils'

import { viewForPath } from './usePipelineViewSync'

export const PipeLineTypeOptions = [PipeLineType.Default, PipeLineType.Servicing, PipeLineType.Fci]

/**
 * The pipeline types this user may work in, and their labels. Shared by the global
 * mode switch in the top header and the pipeline page, so both offer the same list.
 */
export const usePipelineTypeOptions = () => {
  const [auth, vendor] = useSelector((state: any) => [state.auth, state.vendor])
  const { hasPermission } = usePermissions()

  const canManageServicing = hasPermission('Pipeline.View.Servicing')
  const canChangePipelineType = hasPermission('Pipeline.View.PipelineType')

  // Other Nexys companies whose pipelines are shared with this one (admins only).
  const nexysPeerOptions = useMemo(() => {
    if (!userHasAccountType(auth.profile, AccountType.ADMIN)) return {}

    const peers = Array.isArray(vendor?.nexys) ? vendor.nexys : []
    return peers.reduce((acc: Record<string, string>, peer: { name: string; title: string }) => {
      acc[buildPeerPipelineType(peer.name)] = peer.title || peer.name
      return acc
    }, {})
  }, [auth.profile.accountType, vendor?.nexys])

  const eligiblePipelineTypeList: string[] = useMemo(() => {
    const base = !canManageServicing
      ? [PipeLineType.Default, PipeLineType.Servicing]
      : PipeLineTypeOptions.filter((v) => vendor.Fci || v !== PipeLineType.Fci)
    const selected = filterObject(auth.profile?.eligiblePipelineTypes || {})
    const filteredBase = !selected.length ? base : base.filter((type) => selected.includes(type))
    const peerTypes = Object.keys(nexysPeerOptions)
    return [...(filteredBase.length ? filteredBase : base), ...peerTypes]
  }, [auth.profile?.eligiblePipelineTypes, canManageServicing, vendor.Fci, nexysPeerOptions])

  const pipelineTypeOptions: Record<string, string> = useMemo(() => {
    return eligiblePipelineTypeList.reduce((acc: Record<string, string>, type) => {
      acc[type] = nexysPeerOptions[type] || PipeLineTypeLabels[type] || type
      return acc
    }, {})
  }, [eligiblePipelineTypeList, nexysPeerOptions])

  return {
    canChangePipelineType,
    eligiblePipelineTypeList,
    nexysPeerOptions,
    pipelineTypeOptions,
    // Nothing to switch between when the user is confined to a single pipeline.
    canSwitchPipelineType: canChangePipelineType && eligiblePipelineTypeList.length > 1,
  }
}

/**
 * Switch the whole app between Origination and Servicing (and the FCI / shared-company
 * pipelines). The mode lives on `pipelineFilters.pipelineType`, which drives the pipeline
 * grid, the top-header items and the Reports link via `usePipelineViewSync`.
 */
export const usePipelineModeSwitch = () => {
  const dispatch = useDispatch()
  const navigate = useHistory()
  const { pathname } = useLocation()
  const pipelineType = useSelector((state: any) => state.pipelineFilters.pipelineType)
  // One field at a time: the switch sits in the header, so it must not re-render on every
  // unrelated change to the loan currently open.
  const openLoanNumber = useSelector((state: any) => state.loanDetail?.loanNumber)
  const openLoanServicingPipeline = useSelector((state: any) => state.loanDetail?.servicingPipeline)

  /**
   * The same loan's file in the mode we are switching to, or null when there is none to
   * open: the route is not a loan file, the loan was never boarded so it has no servicing
   * file, or the target is FCI / a shared company, which are their own books of loans.
   */
  const loanFileInMode = (nextType: string) => {
    const loanNumber = loanNumberForPath(pathname)
    if (!loanNumber) return null

    if (nextType === PipeLineType.Default) return `/loan_process/overview/${loanNumber}`
    if (nextType !== PipeLineType.Servicing) return null
    // servicingPipeline is only known for the loan the store currently holds.
    if (String(openLoanNumber) !== loanNumber) return null
    if (!isServicingLoan({ servicingPipeline: openLoanServicingPipeline })) return null
    return `/servicing/${loanNumber}?menu=servicingNotes`
  }

  const switchTo = (nextType: string) => {
    if (!nextType || nextType === pipelineType) return

    // A page that belongs to the mode we are leaving would be flipped straight back by
    // usePipelineViewSync (and shows loans the new mode does not own), so move off it
    // first - into the same loan's file in the new mode when it has one, so switching
    // view does not close the file the user is working in, and otherwise the pipeline.
    // Both happen in one event handler, so the route and the mode land in the same
    // render and the sync hook never sees the mismatch.
    const pageMode = viewForPath(pathname)
    if (pageMode && pageMode !== nextType) navigate.push(loanFileInMode(nextType) || '/pipeline')

    // Drop the filters that belong to the old mode; the pipeline refills them with the
    // new mode's defaults.
    dispatch(
      setPipelineFilters({
        pipelineType: nextType,
        status: {},
        orderBy: 'asOf',
        orderDir: '-1',
        lock: '0',
        maturity: '0',
        defaultRate: '0',
        rateMin: '',
        rateMax: '',
        activeOnly: true,
      }),
    )
  }

  return { pipelineType, switchTo }
}
